import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, IconButton, Tooltip, Typography,
} from '@mui/material';
import {
  Add as AddIcon, Assignment as AssignmentIcon, Edit as EditIcon, Print as PrintIcon,
  Replay as ReplayIcon,
} from '@mui/icons-material';
import {
  getPhysiotherapyAssessmentPrintData,
  listPhysiotherapyAssessments,
} from '../../services/physiotherapyAssessmentService';
import type { PhysiotherapyAssessmentRecord } from '../../types/physiotherapyAssessment';
import { buildPhysiotherapyAssessmentPrintHtml } from '../../utils/physiotherapyAssessmentPrintDocument';
import { printClinicHtml } from '../../utils/clinicPrintHtml';
import PhysiotherapyAssessmentEditorDialog, { type PhysiotherapyAssessmentDialogMode } from './PhysiotherapyAssessmentEditorDialog';
import { useAuth } from '../../contexts/AuthContext';

export interface PatientPhysiotherapyAssessmentTabProps {
  patientId: string;
  patient?: any;
  linkedProfessionalId?: string | null;
  linkedAppointmentId?: string | null;
}

const typeLabel = (t: string) => (t === 're_evaluation' ? 'Reavaliação' : 'Avaliação inicial');

const PatientPhysiotherapyAssessmentTab: React.FC<PatientPhysiotherapyAssessmentTabProps> = ({
  patientId,
  patient,
  linkedProfessionalId,
  linkedAppointmentId,
}) => {
  const { user } = useAuth();
  const unitId = user?.current_unit?.id;
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<PhysiotherapyAssessmentRecord[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<PhysiotherapyAssessmentDialogMode>('create');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editorType, setEditorType] = useState<'initial' | 're_evaluation'>('initial');
  const [priorAssessmentId, setPriorAssessmentId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listPhysiotherapyAssessments({
        patient_id: patientId,
        unit_id: unitId,
        limit: 100,
      });
      setItems(res?.data || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao carregar avaliações');
    } finally {
      setLoading(false);
    }
  }, [patientId, unitId]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = (type: 'initial' | 're_evaluation', priorId: string | null = null) => {
    setEditorType(type);
    setSelectedId(null);
    setPriorAssessmentId(priorId);
    setDialogMode('create');
    setDialogOpen(true);
  };

  const openView = (id: string) => {
    setSelectedId(id);
    setPriorAssessmentId(null);
    setDialogMode('view');
    setDialogOpen(true);
  };

  const openEdit = (id: string) => {
    setSelectedId(id);
    setPriorAssessmentId(null);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  const handlePrint = async (id: string, assessmentType: string) => {
    try {
      const printData = await getPhysiotherapyAssessmentPrintData(id);
      const html = buildPhysiotherapyAssessmentPrintHtml(printData);
      printClinicHtml(html);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Erro ao imprimir');
    }
  };

  const latestInitial = items.find((i) => i.assessment_type === 'initial');

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentIcon sx={{ color: '#0d9488', fontSize: 20 }} /> Avaliação Fisioterapêutica
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button size="small" variant="contained" startIcon={<AddIcon />}
            onClick={() => openNew('initial')}
            sx={{ bgcolor: '#0d9488', '&:hover': { bgcolor: '#0f766e' }, textTransform: 'none', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
            Nova avaliação
          </Button>
          <Button size="small" variant="outlined" startIcon={<ReplayIcon />}
            onClick={() => openNew('re_evaluation', latestInitial?.id || null)}
            sx={{ borderColor: '#0d9488', color: '#0d9488', textTransform: 'none', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
            Nova reavaliação
          </Button>
          <Button size="small" variant="outlined" href={`/clinic/physiotherapy-assessments?patient=${patientId}`}
            sx={{ borderColor: '#0d9488', color: '#0d9488', textTransform: 'none', flex: { xs: '1 1 100%', sm: '0 0 auto' } }}>
            Ver todas
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>
      ) : !items.length ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Nenhuma avaliação registrada. Comece pela avaliação inicial para documentar o quadro do paciente.
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {items.map((item) => (
            <Card
              key={item.id}
              onClick={() => openView(item.id)}
              sx={{
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { borderColor: '#0d9488', bgcolor: '#f0fdfa' },
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#0f766e' }}>
                      {typeLabel(item.assessment_type)}
                      {item.assessment_number ? ` · ${item.assessment_number}` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                      {item.assessment_date
                        ? new Date(`${item.assessment_date}T12:00:00`).toLocaleDateString('pt-BR')
                        : '—'}
                      {item.professional_name ? ` · ${item.professional_name}` : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    <Chip
                      size="small"
                      label={item.status === 'finalized' ? 'Finalizada' : 'Rascunho'}
                      sx={{
                        fontSize: '0.65rem', height: 20, fontWeight: 600,
                        bgcolor: item.status === 'finalized' ? '#dcfce7' : '#fef3c7',
                        color: item.status === 'finalized' ? '#16a34a' : '#d97706',
                      }}
                    />
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openEdit(item.id)} sx={{ color: '#0d9488' }} aria-label="Editar avaliação">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Imprimir">
                      <IconButton size="small" onClick={() => handlePrint(item.id, item.assessment_type)} sx={{ color: '#64748b' }} aria-label="Imprimir avaliação">
                        <PrintIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <PhysiotherapyAssessmentEditorDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        patientId={patientId}
        patient={patient}
        existingId={selectedId}
        mode={dialogMode}
        defaultType={selectedId ? undefined : editorType}
        priorAssessmentId={priorAssessmentId}
        linkedProfessionalId={linkedProfessionalId}
        linkedAppointmentId={linkedAppointmentId}
        onSaved={load}
        onRequestEdit={() => selectedId && openEdit(selectedId)}
      />
    </Box>
  );
};

export default PatientPhysiotherapyAssessmentTab;
